import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import Button from "@/components/Common/Button";
import CheckBox from "@/components/Common/CheckBox";
import Modal from "@/components/Common/Modal";
import TextButton from "@/components/Common/TextButton";
import SegmentedToggle from "@/components/Common/forms/SegmentedToggle";
import MiniPlanInfoCard from "@/components/plans/MiniPlanInfoCard";
import usePackages from "@/hooks/usePackages";
import {usePayment} from "@/hooks/usePayment";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import clsxm from "@/lib/clsxm";
import {VAT_PERCENTAGE} from "@/store/constants";
import {isPurchasePlanModalOpenAtom} from "@/store/jotaiAtoms";
import type {TCurrency, TPackageName, TPackagePaymentPeriodType} from "@/store/types";
import useUserStore from "@/store/user";
import {useSetAtom} from "jotai";
import {type FC, useState} from "react";
import {useTranslation} from "react-i18next";
import {useIntercom} from "react-use-intercom";

type TTier = Extract<TPackageName, "ADVANCED" | "PROFESSIONAL" | "EXPERT">;

const TIERS: {labelKey: "packageAdvanced" | "packageProfessional" | "packageExpert"; name: TTier}[] = [
  {labelKey: "packageAdvanced", name: "ADVANCED"},
  {labelKey: "packageProfessional", name: "PROFESSIONAL"},
  {labelKey: "packageExpert", name: "EXPERT"},
];

const RECOMMENDED_TIER: TTier = "PROFESSIONAL";

const PurchasePlanModal: FC = () => {
  const {t} = useTranslation(["search_visibility", "common", "multi_account"]);
  const setIsOpen = useSetAtom(isPurchasePlanModalOpenAtom);
  const {showMessages} = useIntercom();
  const formatPrice = usePriceFormat();
  const {purchasePlan} = usePayment();
  const currency = useUserStore(state => state.user.currency);

  const [selectedTier, setSelectedTier] = useState<TTier>(RECOMMENDED_TIER);
  const [selectedPeriod, setSelectedPeriod] = useState<TPackagePaymentPeriodType>("YEARLY");
  const [isInstagramSelected, setIsInstagramSelected] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const category = isInstagramSelected ? "BOTH" : "TIKTOK";
  const {packagesData} = usePackages(currency as TCurrency, category);
  const selectedPlan = packagesData?.find(p => p.name === selectedTier);
  const isYearly = selectedPeriod === "YEARLY";
  const isSwissBrand = currency === "CHF";

  const handleClose = () => setIsOpen(false);

  return (
    <Modal
      backgroundClassName="max-w-135"
      closeButtonClassName="mt-5 self-start"
      customBottomRow={
        <div className="flex flex-col gap-2.5 p-5">
          <Button
            buttonStyle="Blue"
            className="h-10 w-full sm:h-15"
            isLoading={isPurchasing}
            onClick={() => {
              if (!selectedPlan) return;

              setIsPurchasing(true);
              purchasePlan({
                packageId: selectedPlan.id,
                packageName: selectedTier,
                packageType: selectedPeriod,
                successUrl: window.location.href,
              }).catch(() => setIsPurchasing(false));
            }}>
            {t("multi_account:purchase_plan")}
          </Button>
          <div className="flex items-start gap-2.5">
            <TextButton className="h-8 px-8 text-sm" onClick={handleClose} text={t("common:close")} />
            <TextButton className="h-8 px-8 text-sm" onClick={() => showMessages()} text={t("common:needHelp")} />
          </div>
        </div>
      }
      customTitleRow={
        <div className="flex w-full items-start justify-between p-5">
          <div className="flex flex-col items-start justify-center gap-1 sm:gap-3">
            <div className="bg-primary-powder flex h-5 items-center justify-center rounded-full px-2">
              <span className="text-2xs bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent uppercase">
                {t("plan_selection")}
              </span>
            </div>
            <span className="text-neutral-black text-base leading-3.5 font-semibold sm:text-xl sm:leading-4.5">
              {t("purchase_plan_to_activate_tiktok")}
            </span>
            <span className="text-neutral-black text-xs leading-3 sm:text-sm sm:leading-5">
              {t("to_access_search_visibility_add_tiktok")}
            </span>
          </div>
        </div>
      }
      onClose={handleClose}>
      <MiniPlanInfoCard
        chipsClassName="flex-wrap gap-y-1"
        className="bg-neutral-white-light w-full"
        name={selectedTier}
        packageCategory={category}
        packageType={selectedPeriod}
        pricePerMonth={selectedPlan?.price_per_month}
        pricePerYear={selectedPlan?.price_per_year}
      />
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:gap-4">
        {TIERS.map(({labelKey, name}) => {
          const tierPlan = packagesData?.find(p => p.name === name);
          const isSelected = name === selectedTier;
          const isRecommended = name === RECOMMENDED_TIER;
          const tierPrice = formatPrice(isYearly ? tierPlan?.price_per_year : tierPlan?.price_per_month);

          return (
            <button
              key={name}
              type="button"
              onClick={() => setSelectedTier(name)}
              className={clsxm(
                "relative flex min-h-12 flex-1 cursor-pointer items-center justify-between gap-3 rounded-lg border-2 px-4 py-2 transition-colors sm:min-h-18 sm:flex-col sm:justify-center sm:gap-2 sm:p-4",
                isSelected
                  ? "border-primary-dark-solid bg-primary-light-solid"
                  : "border-neutral-grey-light-dark bg-white",
              )}>
              {isRecommended && (
                <div className="bg-primary-powder absolute top-0 left-1/2 min-h-5 -translate-x-1/2 -translate-y-1/2 rounded-full px-2">
                  <span className="text-2xs bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent uppercase">
                    {t("recommended_badge")}
                  </span>
                </div>
              )}
              <span className="text-neutral-black leading-3 font-semibold">{t(`common:${labelKey}`)}</span>
              <span className="text-neutral-grey-dark text-xs leading-3">{tierPrice}*</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => window.open("/plans", "_blank")}
        className="hover:text-primary-medium-solid text-primary-dark-solid flex h-8 cursor-pointer items-center justify-center gap-2 self-center text-sm font-medium">
        {t("view_all_benefits")}
        <RightArrowIcon className="size-3 shrink-0" />
      </button>
      <div className="flex flex-col gap-2">
        <span className="text-neutral-grey-dark text-xs leading-4">{t("billing")}</span>
        <SegmentedToggle
          titleClassName="capitalize"
          variant="elevated"
          options={[
            {label: t("common:monthly"), onClick: () => setSelectedPeriod("MONTHLY")},
            {label: t("common:yearly"), onClick: () => setSelectedPeriod("YEARLY")},
          ]}
          selectedIndex={selectedPeriod === "MONTHLY" ? 0 : 1}
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-neutral-grey-dark text-xs leading-4">{t("platforms")}</span>
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => setIsInstagramSelected(!isInstagramSelected)}
            className="flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-sm border border-transparent"
            style={{
              backgroundImage: isInstagramSelected
                ? "linear-gradient(var(--color-primary-light-solid), var(--color-primary-light-solid)), linear-gradient(var(--primary-light-gradient))"
                : "linear-gradient(white, white), linear-gradient(var(--color-neutral-grey-light), var(--color-neutral-grey-light))",
              backgroundClip: "padding-box, border-box",
            }}>
            <CheckBox isChecked={isInstagramSelected} />
            <InstagramIcon className="size-3 shrink-0" />
            <span className="text-neutral-black text-sm font-medium">{t("common:instagram")}</span>
          </button>
          <div className="flex flex-1 flex-col gap-3">
            <div
              className="flex h-12 cursor-not-allowed items-center justify-center gap-2 rounded-sm border border-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(var(--color-primary-light-solid), var(--color-primary-light-solid)), linear-gradient(var(--primary-light-gradient))",
                backgroundClip: "padding-box, border-box",
              }}>
              <CheckBox disabled isChecked />
              <TiktokIcon className="size-3 shrink-0" />
              <span className="text-neutral-black text-sm font-medium">{t("common:tiktok")}</span>
            </div>
            <span className="text-neutral-grey-dark text-2xs leading-3 font-semibold uppercase sm:leading-2">
              {t("required_for_tiktok_visibility")}
            </span>
          </div>
        </div>
      </div>
      <span className="text-neutral-grey-dark text-sm">
        {isSwissBrand
          ? t("common:pricesDontIncludeVATSwitzerland", {vatPercentage: VAT_PERCENTAGE})
          : t("common:pricesDontIncludeVAT")}
      </span>
    </Modal>
  );
};

export default PurchasePlanModal;
